import { IS_DEV } from "@config/consts";
import { ADMIN_DASHBOARD_LINK } from "@config/links";
import { PATHS } from "@config/app.routing";

import { MODALS } from "@typings/modals";
import { IOAuthState, OAUTH_PROVIDERS } from "@typings/oauth";
import { USER_TYPES } from "@typings/user";

import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { langFromPathname } from "@services/LangFromPathname";

import { useModalManager } from "@hooks/modal";

import { useAuthDispatch, useAuthSelector } from "./redux";
import {
  connectUser,
  loginUserByAccessToken,
  setUserData,
} from "./redux/oauth/oauth.actions";
import {
  disconnectOAuth as disconnectOAuthAction,
  fetchUserData,
  loginUser,
  logoutUser,
  recoverPassword,
} from "./redux/user/user.actions";
import {
  allowTemporaryLogin as allowTemporaryLoginAction,
  disallowTemporaryLogin as disallowTemporaryLoginAction,
} from "./redux/login/login.actions";
import { getIsTemporaryLoginAllowed } from "./redux/login/login.selectors";
import { useAuthTelemetry, type TriggeredBy } from "./useAuthTelemetry";

let onSuccessCallback: () => void = () => null;

interface SignInOptions {
  username: string;
  password: string;
  rememberMe?: boolean;

  /** use when only selected type of clients should be allowed to authenticate **/
  desiredUserType?: USER_TYPES;
}

type AuthOptions = {
  /**
   * Redirect to this URL after successful login.
   * logic different based on type
   * function: will make call instead redirect.
   * null: no redirect will be performed.
   * undefined: default behaviour
   */
  redirectAfterLogin?: (() => void) | string | null;
};

const defaultOptions: AuthOptions = {
  redirectAfterLogin: undefined,
};

const useAuthManager = () => {
  const language = langFromPathname();
  const dispatch = useAuthDispatch();
  const { showModal, hideModal, isModalActive, getOption } = useModalManager();
  const isTemporaryLoginAllowed = useAuthSelector(getIsTemporaryLoginAllowed);
  const {
    sendLoginEvent,
    sendLogoutEvent,
    sendResetPasswordEvent,
    sendModalEvent,
  } = useAuthTelemetry();

  const options: AuthOptions = {
    ...defaultOptions,
    ...getOption<AuthOptions | undefined>(MODALS.NAVBAR_LOGIN),
  };

  const runSuccessCallbackOnce = () => {
    onSuccessCallback();
    onSuccessCallback = () => null;
  };

  const signIn = async (payload: SignInOptions): Promise<SignInResult> => {
    const { username, password, rememberMe, desiredUserType } = payload;

    sendLoginEvent("email_password", "attempt", {
      desiredUserType,
    });

    const result = await dispatch(
      loginUser({
        userType: desiredUserType,
        username,
        password,
        rememberMe,
      }),
    );

    if (desiredUserType && result.ok) {
      const actualUserType = result.payload.data.userType;

      if (actualUserType !== desiredUserType) {
        sendLoginEvent("email_password", "failure", {
          userType: actualUserType,
          reason: "incorrect_user_type",
        });

        await dispatch(logoutUser());

        const errorKey = "incorrect_account_type_error";

        return {
          error: errorKey,
          ok: false,
          isErrorTranslated: false,
          apiResponseData: null,
          onError: (callback: (error: string) => void) => {
            callback(errorKey);
          },
          onSuccess: () => undefined,
        };
      }
    }

    await dispatch(fetchUserData());

    runSuccessCallbackOnce();

    if (result.ok) {
      sendLoginEvent("email_password", "success", {
        userType: result.payload.data.userType,
      });
      resolvePostLoginRedirect(result.payload.data.userType, {
        redirectAfterLogin: options?.redirectAfterLogin,
      });
    } else {
      sendLoginEvent("email_password", "failure", {
        error: result.errors?.[0]?.message || "unknown-error",
      });
    }

    return {
      error: result.errors?.[0]?.message || null,
      isErrorTranslated: true,
      ok: result.ok,
      apiResponseData: result.payload.data,
      onError: (callback: (error: string | null) => void) => {
        !result.ok && callback(result.errors?.[0]?.message || null);
      },
      onSuccess: (callback: () => void) => {
        result.ok && callback();
      },
    };
  };

  const signInByAccessToken = async (
    provider: OAUTH_PROVIDERS,
    accessToken: string,
    profile: { firstName: string; lastName: string; email: string },
  ): Promise<AsyncActionResult> => {
    sendLoginEvent("oauth", "attempt", {
      provider,
    });

    const resp = await dispatch(loginUserByAccessToken(provider, accessToken));

    resp.onSuccess(async () => {
      setOauthUserData({
        provider,
        accessToken,
        ...profile,
      });

      await dispatch(fetchUserData());
      runSuccessCallbackOnce();

      sendLoginEvent("oauth", "success", {
        provider,
        userType: resp.payload.data.userType,
      });
      resolvePostLoginRedirect(resp.payload.data.userType, {
        redirectAfterLogin: options?.redirectAfterLogin,
      });
    });

    resp.onError(error => {
      sendLoginEvent("oauth", "failure", {
        provider,
        error: error.message || "unknown-error",
      });
    });

    await dispatch(disallowTemporaryLoginAction());

    return resp;
  };

  const resolvePostLoginRedirect = (
    userType: USER_TYPES,
    options: Pick<AuthOptions, "redirectAfterLogin">,
  ) => {
    if (options.redirectAfterLogin === null) return;

    if (typeof options.redirectAfterLogin === "function") {
      options.redirectAfterLogin();
      return;
    }

    if (options.redirectAfterLogin) {
      window.location.href = options.redirectAfterLogin;
      return;
    }

    const isClientApp = window.location.pathname.includes("/client");
    const isAgentApp = window.location.pathname.includes("/agent");

    /**
     * Do nothing in local dev environment, or when user logged in inside one of our apps.
     */
    if (IS_DEV || isClientApp || isAgentApp) return;

    /**
     * Admin logs in on home page or other static page.
     * Redirect to admin dashboard.
     */
    if (userType === USER_TYPES.ADMIN) {
      window.location.href = `/${language}${ADMIN_DASHBOARD_LINK}`;
      return;
    }

    /**
     * Client logs in on home page or other static page.
     * Redirect to client dashboard.
     */
    if (userType === USER_TYPES.CLIENT) {
      window.location.href = `/${language}${PATHS.CLIENT_APP}`;
      return;
    }

    /**
     * Agent logs in on home page or other static page.
     * Redirect to agent app.
     */
    if (userType === USER_TYPES.AGENT) {
      window.location.href = `/${language}${PATHS.AGENT_APP}`;
      return;
    }
  };

  const restorePassword = async (email: string): Promise<AsyncActionResult> => {
    sendResetPasswordEvent("attempt");

    const resp = await dispatch(recoverPassword(email));

    resp.onSuccess(() => {
      sendResetPasswordEvent("success");
    });

    resp.onError(error => {
      sendResetPasswordEvent("failure", {
        error: error.message || "unknown-error",
      });
    });

    return resp;
  };

  const openLoginModal = (options?: AuthOptions) => {
    showModal(MODALS.NAVBAR_LOGIN, options);
    sendModalEvent("open");
  };

  const closeLoginModal = () => {
    hideModal(MODALS.NAVBAR_LOGIN);
    sendModalEvent("close");
  };

  const setOauthUserData = (payload: IOAuthState) => {
    dispatch(setUserData(payload));
  };

  const isLoginModalOpen = () => {
    return isModalActive(MODALS.NAVBAR_LOGIN);
  };

  const logout = async (triggeredBy: TriggeredBy) => {
    sendLogoutEvent("attempt", triggeredBy);

    const resp = await dispatch(logoutUser());

    resp.onSuccess(() => {
      sendLogoutEvent("success", triggeredBy);
    });

    resp.onError(error => {
      sendLogoutEvent("failure", triggeredBy, {
        error: error.message || "unknown-error",
      });
    });

    return resp;
  };

  const allowTemporaryLogin = () => {
    return dispatch(allowTemporaryLoginAction());
  };

  const disallowTemporaryLogin = () => {
    return dispatch(disallowTemporaryLoginAction());
  };

  const disconnectOAuth = (password: string) => {
    return dispatch(disconnectOAuthAction(password));
  };

  const connectOAuthUser = (
    provider: OAUTH_PROVIDERS,
    access_token: string,
  ) => {
    return dispatch(connectUser(provider, access_token));
  };

  const setOnSuccessCallback = (callback: () => void) => {
    onSuccessCallback = callback;
  };

  const reloadUserData = () => {
    return dispatch(fetchUserData());
  };

  return {
    signIn,
    signInByAccessToken,
    isLoginModalOpen,
    openLoginModal,
    closeLoginModal,
    restorePassword,
    setOauthUserData,
    logout,
    isTemporaryLoginAllowed,
    allowTemporaryLogin,
    disallowTemporaryLogin,
    setOnSuccessCallback,
    disconnectOAuth,
    reloadUserData,
    connectOAuthUser,
  };
};

interface SignInResult {
  ok: boolean;
  error: string | null;
  isErrorTranslated: boolean;
  onSuccess: (callback: () => void) => void;
  onError: (callback: (error: string | null) => void) => void;
  apiResponseData: any | null;
}

export { useAuthManager };
